import { ButtonPrimary } from "components/Atoms/Button";
import { PreviewPanelDetailRow } from "components/Atoms/details/PreviewPanelDetailRow";
import React, { useEffect } from "react";
import { PlaceholderContent } from "supportbeacon/articleplaceholders";
import { useApi } from "utilities/hookutils";
import { routes } from "utilities/routing/generatedRoutes";
import { PreviewPanel, PreviewPanelContent, PreviewPanelFooter, PreviewPanelHeader } from "../PreviewPanel";

interface BankAccountPreviewPanelProps {
  bankAccountId: number
}

const BankAccountPreviewPanel = (props: BankAccountPreviewPanelProps) => {
  const { bankAccountId } = props

  const api = useApi(() => window.CoreApi.BankAccount.GetBankAccount(bankAccountId), ["bankAccount", bankAccountId], {
    startEnabled: true
  });

  useEffect(() => {
    api.refetch();
  }, [bankAccountId]);

  if (api.isLoading) {
    return <>
      <PreviewPanelHeader title={RESX.BankAccount.resxBankAccount} />
      <PreviewPanelContent>
        <PlaceholderContent />
      </PreviewPanelContent>
      <PreviewPanelFooter />
    </>;
  }

  const bankAccount = api.data;

  return <>
    <PreviewPanelHeader title={RESX.BankAccount.resxBankAccount} />
    <PreviewPanelContent>
      <div className="flex-column gap-larger">
        <PreviewPanelDetailRow labelTop={RESX.GeneralLabels.InternalTitle}>
          {bankAccount.title}
        </PreviewPanelDetailRow>

        <PreviewPanelDetailRow labelTop={RESX.BankAccount.Format}>
          {serverReference.bankAccountFormats.find(f => f.value === bankAccount.format).text}
        </PreviewPanelDetailRow>

        <PreviewPanelDetailRow labelTop={RESX.Company.BeneficiaryName}>
          {bankAccount.beneficiaryName}
        </PreviewPanelDetailRow>

        {bankAccount.abaRoutingNumber && <PreviewPanelDetailRow labelTop={RESX.BankAccount.RoutingNumber}>
          {bankAccount.abaRoutingNumber}
        </PreviewPanelDetailRow>}

        {bankAccount.institutionNumber && <PreviewPanelDetailRow labelTop={RESX.BankAccount.InstitutionNumber}>
          {bankAccount.institutionNumber}
        </PreviewPanelDetailRow>}

        {bankAccount.accountNumber && <PreviewPanelDetailRow labelTop={RESX.Company.AccountNumber}>
          {bankAccount.accountNumber}
        </PreviewPanelDetailRow>}

        {bankAccount.transitNumber && <PreviewPanelDetailRow labelTop={RESX.BankAccount.TransitNumber}>
          {bankAccount.transitNumber}
        </PreviewPanelDetailRow>}

        {bankAccount.bsbCode && <PreviewPanelDetailRow labelTop={RESX.BankAccount.BsbCode}>
          {bankAccount.bsbCode}
        </PreviewPanelDetailRow>}

        {bankAccount.ibanNumber && <PreviewPanelDetailRow labelTop={RESX.Company.IbanNumber}>
          {bankAccount.ibanNumber}
        </PreviewPanelDetailRow>}

        {bankAccount.sortCode && <PreviewPanelDetailRow labelTop={RESX.BankAccount.SortCode}>
          {bankAccount.sortCode}
        </PreviewPanelDetailRow>}

        {bankAccount.swiftCode && <PreviewPanelDetailRow labelTop={RESX.Company.SwiftCode}>
          {bankAccount.swiftCode}
        </PreviewPanelDetailRow>}

        {bankAccount.bankAddress && <PreviewPanelDetailRow labelTop={RESX.BankAccount.Bank}>
          {bankAccount.bankAddress}
        </PreviewPanelDetailRow>}

        {bankAccount.branchName && <PreviewPanelDetailRow labelTop={RESX.BankAccount.BranchName}>
          {bankAccount.branchName}
        </PreviewPanelDetailRow>}

        {bankAccount.branchCode && <PreviewPanelDetailRow labelTop={RESX.BankAccount.BranchCode}>
          {bankAccount.branchCode}
        </PreviewPanelDetailRow>}

        {bankAccount.memo && <PreviewPanelDetailRow labelTop={RESX.BankAccount.Memo}>
          {bankAccount.memo}
        </PreviewPanelDetailRow>}
      </div>
    </PreviewPanelContent>
    <PreviewPanelFooter>
      {site.permissions.canConfigureSystemSettings && <ButtonPrimary href={routes.bankAccount_Detail(bankAccountId).absolute} label={RESX.Buttons.DetailView} />}
    </PreviewPanelFooter>
  </>;
}

interface BankAccountPreviewProps {
  bankAccountId: number
  trigger: JSX.Element
  fallbackIfNoPermission?: JSX.Element
}

export const BankAccountPreview = (props: BankAccountPreviewProps) => {
  if (!site.permissions.canConfigureSystemSettings) {
    return props.fallbackIfNoPermission || props.trigger;
  }

  return <PreviewPanel
    trigger={props.trigger}
    size="medium"
    id={`bankAccount-preview-${props.bankAccountId}`}>
    <BankAccountPreviewPanel
      bankAccountId={props.bankAccountId}
    />
  </PreviewPanel>;
}